import { Component, Input, OnInit, Output, EventEmitter, OnChanges, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CONSTANTS } from 'src/app/constants';
import { TeamsService } from 'src/app/providers/services/teams.service';
import { TeamMission } from 'src/app/storage/models/team-mission.model';
import { Team } from 'src/app/storage/models/team.model';
import { SelectOption } from '@df/design-system-components';

@Component({
    selector: 'app-add-team',
    templateUrl: './add-team.component.html',
    styleUrls: ['./add-team.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddTeamComponent implements OnInit, OnChanges {
  teams: Team[] = [];
  displayedTeams: Team[] = [];
  locations: IPeopleLocation[] = [];

  locationCodeFilter: string = 'null';
  textFilter: string = '';

  markets = CONSTANTS.REGIONS;
  selectedMarket = CONSTANTS.REGIONS.EUROPE_MIDDLEEAST;
  translations: any;
  marketOptions: SelectOption<string>[] = [];

  @Input() missionTeams: TeamMission[];
  @Input() rolename: string;
  @Input() defaultMarket: string;
  @Output() closeModal = new EventEmitter<any>();
  @Output() addTeam = new EventEmitter<any>();

  constructor(
    private translateService: TranslateService,
    private teamsService: TeamsService
  ) { }

  async ngOnInit(): Promise<void> {
    this.translations = await this.translateService.get('REGIONS').toPromise();
    this.marketOptions = Object.entries(this.markets).map(([key, value]) => ({value, label: this.translations[key]}));
    this.selectedMarket = this.defaultMarket;
    await this.setTeams();
  }

  async ngOnChanges(): Promise<void> {
    this.filterTeams();
  }

  async selectMarket(market: string): Promise<void> {
    this.selectedMarket = market;
    this.textFilter = '';
    this.locationCodeFilter = 'null';
    await this.setTeams();
  }

  close(): void {
    this.closeModal.emit();
  }

  add(team: Team): void {
    this.addTeam.emit(team);
  }

  async setTeams(): Promise<void> {
    // Get market teams
    this.teams = await this.teamsService.getTeamsWithRole({
      marketname: this.selectedMarket,
      rolename: this.rolename
    });
    // Set locations from teams
    this.locations = await this.getLocations();

    this.filterTeams();
  }

  async getLocations(): Promise<IPeopleLocation[]> {
    const translations = await this.translateService.get('ALL').toPromise();
    return this.teams.reduce((acc, cur) => {
      // Check if team location is already in list
      const isLocationInList = acc.find((location: IPeopleLocation) => location.code === cur.peoplelocationcode);
      return isLocationInList ? acc : [...acc, { code: cur.peoplelocationcode, name: cur.peoplelocationname }];
    }, [{ code: 'null', name: translations }]);
  }

  filterTeamsLocation(locationCode: string): void {
    this.locationCodeFilter = locationCode;
    this.filterTeams();
  }

  filterTeams(): void {
    this.displayedTeams = this.teams
      // Filter teams on search value
      .filter(team => {
        const attributesToFilterOn = [
          team.teamMemberId,
          team.teamMemberFirstName,
          team.teamMemberName
        ];

        return attributesToFilterOn.find(attribute =>
          attribute.toUpperCase().includes(this.textFilter.toUpperCase())
        ) != null;
      })
      // Filter teams on location
      .filter(team => (this.locationCodeFilter !== 'null') ? team.peoplelocationcode === this.locationCodeFilter : true)
      // Sort teams
      .sort((a, b) => a.teamMemberFirstName > b.teamMemberFirstName ? 1 : a.teamMemberFirstName === b.teamMemberFirstName ? 0 : -1)
      // Remove selected teams
      .filter(team => this.missionTeams.find(missionTeam => missionTeam.teamMemberId === team.teamMemberId) == null)
      // Remove deactivated teams
      .filter(team => team.isActivated);
  }

}

interface IPeopleLocation {
  code: string;
  name: string;
}
